import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { TIER1_INDICES, WARM_CHAIN_EXPIRIES, nearestChainStrikes } from '@apna/shared'
import { cachedChain, cachedExpiries, catalogueRevision, prefetchUnderlying, subscribeCatalogue } from '@/feed/prefetchChain'
import { SPOT_TOKENS } from '@/feed/spotTokens'
import { tickSocket } from '@/feed/tickSocket'
import { useTickLtps } from '@/feed/useTick'
import { useSnapshot } from '@/feed/useSnapshot'
import { groupIntoLadder } from './useChainCatalogue'

const SPOTS = TIER1_INDICES.map((underlying) => SPOT_TOKENS[underlying]!).filter(Boolean)

/** Keep both front expiries arriving in the tab even while another chain is visible. */
export function useWarmChains(): void {
  const revision = useSyncExternalStore(subscribeCatalogue, catalogueRevision)
  const spots = useTickLtps(SPOTS)
  useEffect(() => {
    for (const underlying of TIER1_INDICES) prefetchUnderlying(underlying)
    return () => tickSocket.setWarm([])
  }, [])
  const tokens = useMemo(() => {
    const wanted = new Set(SPOTS)
    for (const underlying of TIER1_INDICES) {
      for (const expiry of cachedExpiries(underlying)?.expiries.slice(0, WARM_CHAIN_EXPIRIES) ?? []) {
        const chain = cachedChain(underlying, expiry)
        if (!chain) continue
        const rows = groupIntoLadder(chain.items)
        const spot = spots.get(SPOT_TOKENS[underlying]!) ?? rows[Math.floor(rows.length / 2)]?.strike ?? 0
        for (const row of nearestChainStrikes(rows, spot)) {
          for (const contract of [row.ce, row.pe]) {
            const token = Number(contract?.brokerToken)
            if (Number.isInteger(token) && token > 0) wanted.add(token)
          }
        }
      }
    }
    return [...wanted]
  }, [revision, spots])
  const key = tokens.join(',')
  useEffect(() => { tickSocket.setWarm(tokens) }, [key])
  // Initial snapshots race streaming. Split below the snapshot endpoint's cap.
  useSnapshot(tokens.slice(0, 250))
  useSnapshot(tokens.slice(250, 500))
}
